import { AppCardTitle, AppToggleButton } from '@components'
import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { dsColorNew, dataColors } from "@palette"
import { alpha } from "@mui/material/styles"


const DATES = ['15 Mar', '22 Mar', '01 Apr', '10 Apr']

const DEMO_ROWS = [
	{
		name: 'DrivePro',
		prices: [
			{ price: '€48.30', prev: '€46.80', cheapest: false, expensive: false, evo: 'increase', diff: 3.2 },
			{ price: '€52.10', prev: '€54.90', cheapest: false, expensive: false, evo: 'decrease', diff: 5.1 },
			{ price: '€45.70', prev: '€45.70', cheapest: true, expensive: false, evo: null, diff: null },
			{ price: '€58.20', prev: '€55.10', cheapest: false, expensive: false, evo: 'increase', diff: 5.6 },
		],
	},
	{
		name: 'FleetMax',
		prices: [
			{ price: '€42.50', prev: '€44.80', cheapest: true, expensive: false, evo: 'decrease', diff: 5.1 },
			{ price: '€46.90', prev: '€48.20', cheapest: true, expensive: false, evo: 'decrease', diff: 2.7 },
			{ price: '€48.30', prev: '€47.10', cheapest: false, expensive: false, evo: 'increase', diff: 2.5 },
			{ price: '€51.40', prev: '€53.80', cheapest: true, expensive: false, evo: 'decrease', diff: 4.5 },
		],
	},
	{
		name: 'CarFirst',
		prices: [
			{ price: '€55.10', prev: '€50.20', cheapest: false, expensive: true, evo: 'increase', diff: 9.8 },
			{ price: '€58.40', prev: '€55.90', cheapest: false, expensive: true, evo: 'increase', diff: 4.5 },
			{ price: '€52.80', prev: '€54.30', cheapest: false, expensive: false, evo: 'decrease', diff: 2.8 },
			{ price: '€62.10', prev: '€58.70', cheapest: false, expensive: true, evo: 'increase', diff: 5.8 },
		],
	},
	{
		name: 'AutoRent',
		prices: [
			{ price: '€49.80', prev: '€49.80', cheapest: false, expensive: false, evo: null, diff: null },
			{ price: '€53.20', prev: '€51.40', cheapest: false, expensive: false, evo: 'increase', diff: 3.5 },
			{ price: '€47.90', prev: '€50.10', cheapest: false, expensive: false, evo: 'decrease', diff: 4.4 },
			{ price: '€56.70', prev: '€56.70', cheapest: false, expensive: false, evo: null, diff: null },
		],
	},
	{
		name: 'SpeedCar',
		prices: [
			{ price: '€51.60', prev: '€48.90', cheapest: false, expensive: false, evo: 'increase', diff: 5.5 },
			{ price: '€55.80', prev: '€57.10', cheapest: false, expensive: false, evo: 'decrease', diff: 2.3 },
			{ price: '€60.20', prev: '€56.40', cheapest: false, expensive: true, evo: 'increase', diff: 6.7 },
			{ price: '€54.30', prev: '€52.10', cheapest: false, expensive: false, evo: 'increase', diff: 4.2 },
		],
	},
]

// Same cell as the page's BITableCell: meaning colour on the border and a light fill, evolution colours as there
function PriceCell({ data }) {
	const tone = data.cheapest ? dsColorNew.successText : data.expensive ? dsColorNew.dangerRed : null
	const evoColor = data.evo === 'increase' ? dsColorNew.successText : dsColorNew.dangerRed

	return <Box sx={{
		border: '1px solid', borderColor: tone ? alpha(tone, 0.45) : dsColorNew.grayBorder,
		backgroundColor: tone ? alpha(tone, 0.08) : dsColorNew.white,
		borderRadius: '8px', py: 0.5, px: 1, width: 120, minHeight: 45, mx: 'auto', userSelect: 'none',
	}}>
		<Typography sx={{ fontWeight: 700, fontSize: 15, textAlign: 'center', color: dsColorNew.textTitle }}>{data.price}</Typography>
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
			{data.prev && <Typography sx={{ fontSize: 11, color: dsColorNew.textMuted }}>
				{data.prev}
			</Typography>}
			{data.evo && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: evoColor }}>
				<IconSeeker icon={data.evo === 'increase' ? 'arrow-up' : 'arrow-down'} sx={{ fontSize: 10 }}/>
				<Typography sx={{ fontSize: 11, color: 'inherit' }}>{data.diff}%</Typography>
			</Box>}
		</Box>
	</Box>
}

function ChartPlaceholder() {
	const series = [
		{ name: 'DrivePro', color: dsColorNew.webBlue500, points: [48, 52, 46, 58] },
		{ name: 'FleetMax', color: dataColors.green600, points: [42, 47, 48, 51] },
		{ name: 'CarFirst', color: dsColorNew.warningBar, points: [55, 58, 53, 62] },
		{ name: 'AutoRent', color: dataColors.violet500, points: [50, 53, 48, 57] },
		{ name: 'SpeedCar', color: dataColors.red500, points: [52, 56, 60, 54] },
	]
	const max = 65, min = 40
	const range = max - min

	return <Card>
		<CardContent>
			<AppCardTitle icon="chart-line" title="Chart" sx={{ mb: 2 }}/>
			<Box sx={{ position: 'relative', height: 200, pl: 4, pr: 2 }}>
				{/* Y axis labels */}
				{[max, Math.round((max + min) / 2), min].map((val, i) => (
					<Typography key={i} sx={{
						position: 'absolute', left: 0, fontSize: 11, color: dsColorNew.textMuted,
						top: `${(1 - (val - min) / range) * 100}%`, transform: 'translateY(-50%)',
					}}>€{val}</Typography>
				))}
				{/* Grid lines */}
				{[0, 0.5, 1].map((pos, i) => (
					<Box key={i} sx={{
						position: 'absolute', left: 30, right: 0, top: `${pos * 100}%`,
						borderBottom: `1px dashed ${dsColorNew.grayBorder}`,
					}} />
				))}
				{/* SVG lines */}
				<svg width="100%" height="100%" viewBox="0 0 400 200" preserveAspectRatio="none" style={{ position: 'absolute', left: 30, top: 0, width: 'calc(100% - 45px)' }}>
					{series.map((s, si) => {
						const pts = s.points.map((p, i) => `${(i / 3) * 380 + 10},${(1 - (p - min) / range) * 190 + 5}`).join(' ')
						return <polyline key={si} points={pts} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" />
					})}
				</svg>
			</Box>
			{/* X axis */}
			<Box sx={{ display: 'flex', justifyContent: 'space-around', pl: 4, mt: 1 }}>
				{DATES.map((d, i) => (
					<Typography key={i} sx={{ fontSize: 11, color: dsColorNew.textMuted }}>{d}</Typography>
				))}
			</Box>
			{/* Legend */}
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
				{series.map((s, i) => (
					<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Box sx={{ width: 14, height: 3, bgcolor: s.color, borderRadius: 1 }} />
						<Typography sx={{ fontSize: 12, color: dsColorNew.textDark }}>{s.name}</Typography>
					</Box>
				))}
			</Box>
		</CardContent>
	</Card>
}


export default function BIDemoData() {
	return <Grid container spacing={2}>
		{/* Price Comparison Table */}
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
						<AppCardTitle icon="table-cells" title="Price Comparison"/>
						{/* The page's table actions box (BITableActions): the toggle, then the office filter when there are several */}
						<Box sx={{ gap: 1, display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
							<AppToggleButton value="min" onChange={() => {}} buttonProps={{ sx: { px: 2.5, py: 1, width: 100 } }} tabs={[{ value: 'min', label: 'Min. Price' }, { value: 'avg', label: 'Avg. Price' }, { value: 'max', label: 'Max. Price' }]}/>
						</Box>
					</Box>
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ '& th': { textAlign: 'center' } }}>
									<TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, textAlign: 'left !important' }}>Provider</TableCell>
									{DATES.map(d => <TableCell key={d}>{d}</TableCell>)}
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_ROWS.map((row, ri) => (
									<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt }}>
										<TableCell sx={{
											position: 'sticky', left: 0, zIndex: 1,
											fontSize: 13, bgcolor: 'inherit',
										}}>
											{row.name}
										</TableCell>
										{row.prices.map((p, pi) => (
											<TableCell key={pi} sx={{ textAlign: 'center', px: 0.5 }}>
												<PriceCell data={p} />
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>

		{/* Chart */}
		<Grid item xs={12}>
			<ChartPlaceholder />
		</Grid>
	</Grid>
}
